/**
 * useLandingAreas – fetch public areas for the landing areas carousel.
 * Resolves cover images via media_assets (owner_type='area', kind='cover') — canonical SoT.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { resolveAssetUrl } from '@/lib/media/mediaApi';
import type { MediaAsset } from '@/lib/media/mediaTypes';

export interface LandingArea {
  id: string;
  name: string;
  slug: string;
  descriptionShort: string | null;
  coverUrl: string | null;
  href: string;
}

export function useLandingAreas() {
  return useQuery<LandingArea[]>({
    queryKey: ['landing-areas'],
    queryFn: async () => {
      const { data: areas, error } = await supabase
        .from('areas')
        .select('id, name, slug, description_short')
        .eq('status', 'public')
        .order('name', { ascending: true })
        .limit(5);

      if (error) throw error;
      if (!areas || areas.length === 0) return [];

      // Resolve cover images via media_assets (canonical SoT)
      const areaIds = areas.map((a) => a.id);
      const { data: coverAssets } = await supabase
        .from('media_assets')
        .select('*')
        .eq('owner_type', 'area')
        .eq('kind', 'cover')
        .in('owner_id', areaIds)
        .order('sort_order', { ascending: true });

      const coverMap = new Map<string, string>();
      for (const row of (coverAssets ?? []) as unknown as MediaAsset[]) {
        if (row.owner_id && !coverMap.has(row.owner_id)) {
          const url = resolveAssetUrl(row, 640);
          if (url) coverMap.set(row.owner_id, url);
        }
      }

      return areas.map((a) => ({
        id: a.id,
        name: a.name,
        slug: a.slug,
        descriptionShort: a.description_short,
        coverUrl: coverMap.get(a.id) ?? null,
        href: `/omraden/${a.slug}`,
      }));
    },
    staleTime: 10 * 60 * 1000,
  });
}
